import React from 'react';

export type ViewMode = 'grid' | 'heatmap' | 'transpose' | 'patentMap' | 'analyse' | 'sar' | 'targetUniverse';

interface ViewModeSwitcherProps {
  activeMode: ViewMode;
  onModeChange: (mode: ViewMode) => void;
}

/** Toggle modes — click to activate, click again to deactivate (returns to grid) */
const toggleModes: { id: ViewMode; label: string; tooltip: string; icon: React.ReactNode }[] = [
  {
    id: 'heatmap',
    label: 'Heatmap',
    tooltip: 'Heatmap — Colour-coded overlay showing property value ranges',
    icon: (
      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1H5a1 1 0 01-1-1V5zm10 0a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1V5zM4 15a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1H5a1 1 0 01-1-1v-4zm10 0a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1v-4z" />
      </svg>
    ),
  },
  {
    id: 'transpose',
    label: 'Transpose',
    tooltip: 'Transpose — Flip rows and columns, one molecule per column',
    icon: (
      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
      </svg>
    ),
  },
];

const ViewModeSwitcher: React.FC<ViewModeSwitcherProps> = ({ activeMode, onModeChange }) => {
  // Map non-toggle modes to 'grid' for highlight purposes
  const displayMode = ['grid', 'heatmap', 'transpose'].includes(activeMode) ? activeMode : 'grid';

  return (
    <div className="inline-flex items-center bg-white/10 rounded-lg border border-white/20 p-0.5">
      {toggleModes.map((mode) => {
        const isActive = displayMode === mode.id;
        return (
          <button
            key={mode.id}
            onClick={() => onModeChange(isActive ? 'grid' : mode.id)}
            className={`flex items-center gap-1.5 px-2.5 py-1 text-xs rounded-md transition-all whitespace-nowrap ${
              isActive
                ? 'bg-white text-teal-700 shadow-sm font-semibold'
                : 'text-white/60 hover:text-white hover:bg-white/10'
            }`}
            title={mode.tooltip}
          >
            {mode.icon}
            <span className="hidden sm:inline">{mode.label}</span>
          </button>
        );
      })}
    </div>
  );
};

export default ViewModeSwitcher;
